import { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { deleteEvidence } from '../../actions/evidencesActions';
import {
  Grid, Card, Text, Box, Table, Badge, Flex, Group, ThemeIcon,
  ActionIcon, Menu, Stack, TextInput, MultiSelect, Loader, Center,
} from '@mantine/core';
import {
  IconDotsVertical, IconDownload, IconEdit, IconEye,
  IconFileCheck, IconSearch, IconTrash,
} from '@tabler/icons-react';
import ModalDialog from '../ModalDialog';
import ModalEvidence from '../ModalEvidence';
import { fetchFile, downloadFile, base64ToBlob } from '../../utils/fileService';
import dayjs from 'dayjs';

const ListedEvidences = ({ editEvidenceEnabled = false }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const { data: evidences, loading: loadingEvidences } = useSelector((state) => state.evidence);
  const { data: peiContainer, loading: loadingPei } = useSelector((state) => state.pei.matrixPei);

  const pei =
    (peiContainer && (peiContainer.matrixPei || peiContainer)) || {};

  const loading = evidences.length === 0 && (loadingEvidences || loadingPei);

  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [evidenceModalOpened, setEvidenceModalOpened] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedKpis, setSelectedKpis] = useState([]);

  const handleSelectEvidence = (evidence, action) => {
    setSelectedEvidence(evidence);
    if (evidence && typeof action === 'function') action();
  };

  const handleDeleteEvidence = (idEvidence) => {
    dispatch(deleteEvidence(idEvidence, user)).then(() => setDeleteModalOpened(false));
  };

  const handleDownloadEvidence = async (fileId, fileName, fileType) => {
    const fileData = await fetchFile(fileId, user.token);
    if (!fileData) return;
    const fileBlob = base64ToBlob(fileData, fileType);
    downloadFile(fileBlob, fileName);
  };

  const allKpis =
    pei?.Objetivos?.flatMap((objetivo) => objetivo?.criterios?.flatMap((c) => c?.kpiList || []) || []) || [];

  const kpiOptions = allKpis.map((kpi) => ({
    value: String(kpi.Id),
    label: `${kpi.numeroCompleto}. ${kpi.titulo}`,
  }));

  const filteredEvidences = useMemo(() => {
    return evidences.filter((ev) => {
      const term = searchValue.toLowerCase();

      const matchesSearch =
        term === '' ||
        ev.titulo?.toLowerCase().includes(term) ||
        ev.descripcion?.toLowerCase().includes(term) ||
        ev.vc_insuser?.toLowerCase().includes(term) ||
        ev.unidadesGestion?.some((ug) =>
          `${ug.codigoUnidadGestion}-${ug.codigoSede}`.toLowerCase().includes(term)
        );

      const matchesKpis =
        selectedKpis.length === 0 || ev.kpis?.some((k) => selectedKpis.includes(String(k.Id)));

      return matchesSearch && matchesKpis;
    });
  }, [evidences, searchValue, selectedKpis]);

  const canEdit = (ev) =>
    editEvidenceEnabled && !ev.tieneValoracion && ev.vc_insuser?.toLowerCase() === user.usermail?.toLowerCase();

  return (
    <Box>
      <Card radius="md" withBorder>
        <Text size="xl" fw={600}>
          Evidencias ({filteredEvidences.length})
        </Text>

        <Box mt="lg">
          <Stack gap="lg">
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6, md: 8 }}>
                <TextInput
                  placeholder="Buscar por evidencia, usuario, unidad de gestión..."
                  leftSection={<IconSearch size={18} />}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.currentTarget.value)}
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <MultiSelect
                  placeholder="Filtrar por KPIs..."
                  data={kpiOptions}
                  value={selectedKpis}
                  onChange={setSelectedKpis}
                  checkIconPosition="right"
                  searchable
                  clearable
                  maxDropdownHeight={150}
                />
              </Grid.Col>
            </Grid>

            <Table.ScrollContainer minWidth={500} maxHeight="60vh">
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th fw={500} maw="35%">Nombre</Table.Th>
                    <Table.Th fw={500} maw="15%">Unidad de Gestión</Table.Th>
                    <Table.Th fw={500} maw="30%">KPIs seleccionados</Table.Th>
                    <Table.Th fw={500} maw="10%">Usuario</Table.Th>
                    <Table.Th fw={500} maw="10%">Fecha</Table.Th>
                    <Table.Th fw={500} maw="10%">Acciones</Table.Th>
                  </Table.Tr>
                </Table.Thead>

                <Table.Tbody>
                  {loading ? (
                    <Table.Tr>
                      <Table.Td colSpan={6}>
                        <Center><Loader color="gray" type="dots" /></Center>
                      </Table.Td>
                    </Table.Tr>
                  ) : filteredEvidences.length > 0 ? (
                    filteredEvidences
                      .sort((a, b) => new Date(b.dt_lu) - new Date(a.dt_lu))
                      .map((ev) => (
                        <Table.Tr key={ev.Id}>
                          <Table.Td
                            onClick={() => handleSelectEvidence(ev, () => setEvidenceModalOpened(true))}
                            style={{ cursor: 'pointer' }}
                          >
                            <Group gap="xs" wrap="nowrap">
                              {ev.tieneValoracion && (
                                <ThemeIcon color="green" variant="light" radius="lg">
                                  <IconFileCheck style={{ width: '70%', height: '70%' }} />
                                </ThemeIcon>
                              )}
                              <Box maw="400px">
                                <Text inherit>{ev.titulo}</Text>
                                <Text inherit lineClamp={3} c="dimmed">{ev.descripcion}</Text>
                              </Box>
                            </Group>
                          </Table.Td>

                          <Table.Td>
                            {ev.unidadesGestion?.[0]?.codigoUnidadGestion}-{ev.unidadesGestion?.[0]?.codigoSede}
                          </Table.Td>

                          <Table.Td>
                            <Flex gap="xs" wrap="wrap">
                              {ev.kpis?.map((kpi) => {
                                const k = allKpis.find((item) => item.Id === kpi.Id);
                                return (
                                  <Badge key={kpi.Id} variant="dot" color="#e72166" title={k?.titulo} maw={250}>
                                    <Text inherit truncate="end">
                                      {k?.numeroCompleto}. {k?.titulo}
                                    </Text>
                                  </Badge>
                                );
                              })}
                            </Flex>
                          </Table.Td>

                          <Table.Td>{ev.vc_insuser}</Table.Td>
                          <Table.Td>{dayjs(ev.dt_lu).format('DD/MM/YYYY')}</Table.Td>

                          <Table.Td ta="center">
                            <Group wrap="nowrap" gap="xs">
                              {canEdit(ev) && (
                                <ActionIcon
                                  variant="subtle"
                                  color="gray.9"
                                  title="Editar"
                                  onClick={() => navigate(`/backoffice/evidences/edit/${ev.Id}`)}
                                >
                                  <IconEdit style={{ width: '70%', height: '70%' }} stroke={2} />
                                </ActionIcon>
                              )}

                              <Menu shadow="md" withArrow>
                                <Menu.Target>
                                  <ActionIcon variant="subtle" color="gray.9" title="Menú">
                                    <IconDotsVertical style={{ width: '70%', height: '70%' }} stroke={2} />
                                  </ActionIcon>
                                </Menu.Target>
                                <Menu.Dropdown>
                                  <Menu.Item
                                    leftSection={<IconEye size={14} />}
                                    onClick={() => handleSelectEvidence(ev, () => setEvidenceModalOpened(true))}
                                  >
                                    Ver
                                  </Menu.Item>

                                  <Menu.Item
                                    leftSection={<IconDownload size={14} />}
                                    onClick={() => {
                                      const principal = ev.archivos?.find((a) => a.esPrincipal);
                                      if (principal) {
                                        handleDownloadEvidence(principal.Id, principal.nombre, principal.tipoArchivo);
                                      }
                                    }}
                                  >
                                    Descargar evidencia
                                  </Menu.Item>

                                  {canEdit(ev) && (
                                    <>
                                      <Menu.Divider />
                                      <Menu.Item
                                        color="red"
                                        leftSection={<IconTrash size={14} />}
                                        onClick={() => handleSelectEvidence(ev, () => setDeleteModalOpened(true))}
                                      >
                                        Eliminar
                                      </Menu.Item>
                                    </>
                                  )}
                                </Menu.Dropdown>
                              </Menu>
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      ))
                  ) : (
                    <Table.Tr>
                      <Table.Td colSpan={6} ta="center">
                        No se han encontrado evidencias.
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Stack>
        </Box>
      </Card>

      <ModalDialog
        opened={deleteModalOpened}
        onClose={() => setDeleteModalOpened(false)}
        onConfirm={() => selectedEvidence && handleDeleteEvidence(selectedEvidence.Id)}
        title="Eliminar evidencia"
        description={<Text span>¿Esta seguro de eliminar la evidencia?</Text>}
        confirmColor="#e72121ff"
      />

      <ModalEvidence
        opened={evidenceModalOpened}
        onClose={() => setEvidenceModalOpened(false)}
        evidence={selectedEvidence}
      />
    </Box>
  );
};

export default ListedEvidences;
